import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import Scripts from './pages/Scripts';
import Content from './pages/Content';
import Calendar from './pages/Calendar';
import Goals from './pages/Goals';
import Team from './pages/Team';
import Music from './pages/Music';
import Releases from './pages/Releases';
import Ads from './pages/Ads';
import Hypersigil from './pages/Hypersigil';
import { VocalProtocols, AdminProtocols } from './pages/Protocols';
import GameConsole from './pages/GameConsole';
import { db } from './lib/firebase';
import { collection, addDoc, doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import './styles/globals.css';

// ── Quick Capture (global, shown on all pages except /wargames) ──
const CAPTURE_TYPES = [
  { id:'content', label:'Content Idea', emoji:'🎬', desc:'Goes to Content Pillars' },
  { id:'music',   label:'Music Idea',   emoji:'🎵', desc:'Goes to Music Garden as Seed' },
  { id:'task',    label:'Task',         emoji:'✅', desc:'Goes to Task Board' },
  { id:'general', label:'General',      emoji:'💭', desc:'Saved to inbox for later' },
];

function QuickCapture({ profile, activeArtistId }) {
  const location = useLocation();
  const [show, setShow]     = useState(false);
  const [form, setForm]     = useState({ text:'', type:'content', artistId: activeArtistId||'' });

  if (location.pathname === '/wargames') return null;

  const handle = async () => {
    if (!form.text.trim()) return;
    const artistId = form.artistId || activeArtistId || 'avidx';
    try {
      if (form.type === 'task') {
        await addDoc(collection(db, 'tasks'), { title:form.text, status:'todo', priority:'P1', artistId, createdBy:profile?.email, createdAt:serverTimestamp() });
      } else if (form.type === 'music') {
        await addDoc(collection(db, 'songs'), { title:form.text, stage:'seed', artistId, createdBy:profile?.email, createdAt:serverTimestamp() });
      } else if (form.type === 'content') {
        const ref = doc(db, 'content', artistId);
        const snap = await new Promise(res => { const u = onSnapshot(ref, s => { u(); res(s); }); });
        const existing = snap.exists() ? snap.data() : {};
        const unsorted = existing?.ideas?.unsorted || [];
        await setDoc(ref, { ideas: { ...(existing.ideas||{}), unsorted: [...unsorted, { id: Date.now().toString(), hook:form.text, caption:'', format:'', captureType:'', songLink:'', status:'idea', links:[] }] } }, { merge:true });
      } else {
        await addDoc(collection(db, 'inbox'), { text:form.text, artistId, createdBy:profile?.email, createdAt:serverTimestamp() });
      }
      setShow(false);
      setForm({ text:'', type:'content', artistId: activeArtistId||'' });
    } catch(e) { console.error('quick capture:', e); }
  };

  return (
    <>
      <button onClick={() => setShow(true)}
        style={{ position:'fixed', bottom:28, right:28, width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg,var(--purple),var(--orange))', border:'none', cursor:'pointer', fontSize:22, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 20px rgba(123,79,196,.4)', zIndex:200, transition:'transform .15s, box-shadow .15s' }}
        onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.boxShadow='0 6px 28px rgba(123,79,196,.6)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 20px rgba(123,79,196,.4)'; }}
        title="Quick Capture">✦</button>

      {show && (
        <div style={{ position:'fixed', inset:0, zIndex:999, background:'rgba(0,0,0,.7)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={() => setShow(false)}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20, padding:28, maxWidth:440, width:'100%' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:'var(--muted)', fontFamily:'var(--mono)', marginBottom:6 }}>Quick Capture</div>
            <div style={{ fontSize:16, fontWeight:800, marginBottom:20 }}>What's the idea?</div>
            <textarea rows={3} value={form.text} onChange={e => setForm(f=>({...f,text:e.target.value}))}
              placeholder="Hook, lyric, task, thought... anything." autoFocus
              style={{ width:'100%', marginBottom:16, fontSize:14, lineHeight:1.7, borderRadius:10, padding:'12px 14px', background:'var(--surface2)', border:'1px solid var(--border)', color:'var(--text)', resize:'none' }}/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
              {CAPTURE_TYPES.map(ct => (
                <div key={ct.id} onClick={() => setForm(f=>({...f,type:ct.id}))}
                  style={{ padding:'10px 14px', borderRadius:10, background:form.type===ct.id?'rgba(123,79,196,.15)':'var(--surface2)', border:`1px solid ${form.type===ct.id?'var(--purple)':'var(--border)'}`, cursor:'pointer', transition:'all .15s' }}>
                  <div style={{ fontSize:16, marginBottom:2 }}>{ct.emoji}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:form.type===ct.id?'var(--purple2)':'var(--text)', marginBottom:1 }}>{ct.label}</div>
                  <div style={{ fontSize:9, color:'var(--dim)', fontFamily:'var(--mono)' }}>{ct.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShow(false)} className="btn btn-ghost">Cancel</button>
              <button onClick={handle} disabled={!form.text.trim()} className="btn btn-primary">Capture ✦</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Layout() {
  const { user, loading, isManager, profile } = useAuth();
  const [activeArtistId, setActiveArtistId] = useState(null);
  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--muted)', position:'relative', zIndex:1 }}><div style={{ textAlign:'center' }}><div style={{ fontSize:32, marginBottom:12 }}>🛸</div><div style={{ fontFamily:'var(--mono)', fontSize:12, letterSpacing:2 }}>LOADING...</div></div></div>;
  if (!user) return <Navigate to="/login" replace/>;
  const effectiveId = isManager ? activeArtistId : profile?.artistId;
  return (
    <div style={{ display:'flex', minHeight:'100vh', position:'relative', zIndex:1 }}>
      <Sidebar activeArtistId={effectiveId} setActiveArtistId={setActiveArtistId}/>
      <main style={{ flex:1, display:'flex', flexDirection:'column', minHeight:'100vh', overflowX:'hidden' }}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard activeArtistId={effectiveId}/>}/>
          <Route path="/tasks" element={<Tasks activeArtistId={effectiveId}/>}/>
          <Route path="/goals" element={<Goals activeArtistId={effectiveId}/>}/>
          <Route path="/calendar" element={<Calendar activeArtistId={effectiveId}/>}/>
          <Route path="/scripts" element={<Scripts activeArtistId={effectiveId}/>}/>
          <Route path="/content" element={<Content activeArtistId={effectiveId}/>}/>
          <Route path="/music" element={<Music activeArtistId={effectiveId}/>}/>
          <Route path="/releases" element={<Releases activeArtistId={effectiveId}/>}/>
          <Route path="/ads" element={<Ads activeArtistId={effectiveId}/>}/>
          <Route path="/hypersigil" element={<Hypersigil activeArtistId={effectiveId}/>}/>
          <Route path="/vocal" element={<VocalProtocols/>}/>
          <Route path="/admin-protocols" element={<AdminProtocols/>}/>
          <Route path="/wargames" element={<GameConsole/>}/>
          {isManager&&<Route path="/team" element={<Team/>}/>}
          <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </main>
      <QuickCapture profile={profile} activeArtistId={effectiveId}/>
    </div>
  );
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace/>;
  return <Login/>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginGate/>}/>
          <Route path="/*" element={<Layout/>}/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}